/* ============================================================================
   Cãna — Reserva release pages.
   Loaded by layout/theme.liquid for any page template whose suffix contains
   "reserva" (templates/page.gold-reserva-1.json, templates/page.reserva.json).

   Six small behaviours, each guarded so it no-ops when its section is absent
   (sections are individually removable in the theme editor):

     · sub-nav scroll-spy          · tasting-note accordion (mobile only)
     · specimen inspector          · quantity + add-to-cart
     · sticky mobile CTA           · marquee pause when off screen

   Scrolling itself is CSS (`scroll-behavior` + `scroll-margin-top`), so the
   anchors work with JS disabled.
   ============================================================================ */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ nav ---
     Active section = the one crossing 42% of the viewport, the rule the design
     prototype used. Done with an IntersectionObserver rather than a scroll
     handler: no per-frame work, and it does not care which element is the
     scroll container — the theme sets `overflow-x: hidden` on <body>, which can
     move the scrollport and leave a window scroll listener silent. */
  function initSubnav(root) {
    var links = Array.prototype.slice.call(
      root.querySelectorAll('.gold-subnav__link'),
    );
    if (!links.length) return;

    var targets = links
      .map(function (link) {
        var id = (link.getAttribute('href') || '').replace('#', '');
        return { link: link, el: id ? document.getElementById(id) : null };
      })
      .filter(function (entry) {
        return entry.el;
      });

    if (!targets.length) return;

    /* Below 768px the bar is a horizontally scrollable chip row and the chips
       are wider than the phone — with the Home chip in front, Numeração and
       Reserva start off-screen. Lighting one there is invisible, so the row has
       to follow the page. Above 768px the track is a centred pill with
       `overflow: visible` and nothing to scroll, which the width test below
       makes a no-op rather than a breakpoint this file has to know about.

       scrollLeft on the track, not scrollIntoView: the latter also scrolls the
       nearest scrollable ancestor vertically, which would fight the very page
       scroll that triggered this. */
    var track = root.querySelector('.gold-subnav__track');

    var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function revealChip(link) {
      if (!track) return;

      var slack = track.scrollWidth - track.clientWidth;
      if (slack <= 1) return;

      /* Measured off rects rather than offsetLeft: the track sets no
         `position`, so a chip's offsetParent is the fixed `.gold-subnav` above
         it and offsetLeft would not be in the track's own scroll coordinates.

         Centre the chip in the track, clamped to the ends so the first and last
         chips sit flush rather than leaving a gap. */
      var trackRect = track.getBoundingClientRect();
      var linkRect = link.getBoundingClientRect();
      var chipStart = linkRect.left - trackRect.left + track.scrollLeft;

      var left = chipStart - (track.clientWidth - linkRect.width) / 2;
      if (left < 0) left = 0;
      if (left > slack) left = slack;

      if (Math.abs(left - track.scrollLeft) < 2) return;

      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
      } else {
        track.scrollLeft = left;
      }
    }

    function paint(active) {
      targets.forEach(function (entry, i) {
        if (i === active) entry.link.setAttribute('aria-current', 'true');
        else entry.link.removeAttribute('aria-current');
      });

      if (targets[active]) revealChip(targets[active].link);
    }

    paint(0);

    if (!('IntersectionObserver' in window)) return;

    /* A zero-height band at 42% of the viewport. The sections tile the page, so
       exactly one crosses it at a time. */
    var visible = [];

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = targets.findIndex(function (t) {
            return t.el === entry.target;
          });
          if (i > -1) visible[i] = entry.isIntersecting;
        });

        var active = -1;
        for (var i = 0; i < targets.length; i++) {
          if (visible[i]) active = i;
        }

        /* Past the last section nothing crosses the band; keep what was lit. */
        if (active > -1) paint(active);
      },
      { rootMargin: '-42% 0px -58% 0px', threshold: 0 },
    );

    targets.forEach(function (entry) {
      observer.observe(entry.el);
    });
  }

  /* -------------------------------------------------------------- tasting ---
     Only an accordion below 768px. Above it CSS shows every body and makes the
     control inert, so the button also drops out of the accessibility tree —
     announcing "collapsed" over copy that is plainly visible would be a lie.

     The mobile collapse is gated on `gold-notes--live`, added here as the last
     thing this function does. Nothing else hides the bodies, so if the script
     never lands the notes simply read as prose. */
  function initNotes(root) {
    var notes = root.querySelectorAll('.gold-note');
    if (!notes.length) return;

    var mq = window.matchMedia('(max-width: 767px)');

    Array.prototype.forEach.call(notes, function (note) {
      var toggle = note.querySelector('.gold-note__toggle');
      var mark = note.querySelector('.gold-note__mark');
      if (!toggle) return;

      function paint() {
        var open = note.getAttribute('data-open') === 'true';

        if (mq.matches) {
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          toggle.removeAttribute('aria-disabled');
          toggle.removeAttribute('tabindex');
        } else {
          toggle.removeAttribute('aria-expanded');
          /* Keep a real button element — role=presentation on a button is invalid.
             Desktop shows all bodies via CSS; the control is inert. */
          toggle.setAttribute('aria-disabled', 'true');
          toggle.setAttribute('tabindex', '-1');
        }

        if (mark) mark.textContent = open ? '\u2013' : '+';
      }

      toggle.addEventListener('click', function () {
        var open = note.getAttribute('data-open') === 'true';
        note.setAttribute('data-open', open ? 'false' : 'true');
        paint();
      });

      if (mq.addEventListener) mq.addEventListener('change', paint);
      else if (mq.addListener) mq.addListener(paint);

      paint();
    });

    /* Last, deliberately. boot() runs each init inside a try/catch, so a throw
       anywhere above leaves this class off and every body visible. */
    var container = root.querySelector('.gold-notes');
    if (container) container.classList.add('gold-notes--live');
  }

  /* ------------------------------------------------------------- specimen ---
     _num and _key are ported verbatim from the design prototype. The key is a
     deterministic FNV-style hash followed by xorshift32, so a given bottle
     number always produces the same key — Nº 0143 must match the artboard.

     The edition size and the series salt come off the input as data attributes
     so a second release can set its own without touching this file. The
     defaults are Reserva Nº 1's values, which is what the original template's
     markup — carrying neither attribute — still gets. */
  var SPECIMEN_TOTAL = 999;
  var SPECIMEN_SERIES = 'CANA-GOLD-R1';

  function specimenNumber(raw, total) {
    var cap = total || SPECIMEN_TOTAL;
    var n = parseInt(String(raw).replace(/\D/g, ''), 10);
    if (!n || n < 1) return 1;
    return n > cap ? cap : n;
  }

  function specimenKey(n, series) {
    var h = 0x9f4c21a8;
    var seed = (series || SPECIMEN_SERIES) + '-' + n;
    for (var i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    var out = [];
    for (var j = 0; j < 4; j++) {
      h ^= h << 13;
      h >>>= 0;
      h ^= h >> 17;
      h ^= h << 5;
      h >>>= 0;
      out.push(h.toString(16).toUpperCase().padStart(8, '0').slice(0, 4));
    }
    return out.join(' · ');
  }

  function initSpecimen(root) {
    var input = root.querySelector('.gold-specimen__input');
    var random = root.querySelector('.gold-specimen__random');
    var display = root.querySelector('[data-gold-specimen-no]');
    var keyEl = root.querySelector('[data-gold-specimen-key]');
    if (!input || !display || !keyEl) return;

    var total =
      parseInt(input.getAttribute('data-gold-specimen-total'), 10) ||
      SPECIMEN_TOTAL;
    var series =
      input.getAttribute('data-gold-specimen-series') || SPECIMEN_SERIES;
    var prefix = display.getAttribute('data-gold-specimen-prefix') || 'Nº';

    /* Pad to the width of the edition size, so the display and the field agree:
       999 bottles reads Nº 143, 9999 reads Nº 0143. */
    var width = String(total).length;

    function paint() {
      var n = specimenNumber(input.value, total);
      display.textContent = prefix + ' ' + String(n).padStart(width, '0');
      keyEl.textContent = specimenKey(n, series);
    }

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, width);
      paint();
    });

    if (random) {
      random.addEventListener('click', function () {
        input.value = String(1 + Math.floor(Math.random() * total)).padStart(
          width,
          '0',
        );
        paint();
      });
    }

    paint();
  }

  /* -------------------------------------------------------------- reserva ---
     Adds to the theme's own cart and opens its drawer. Progressive enhancement
     over a native /cart/add form so the page still sells when JS is blocked. */
  function initReserve(root) {
    var panel = root.querySelector('[data-gold-reserve]');
    if (!panel) return;

    var form = panel.querySelector('[data-gold-reserve-form]');
    var configEl = root.querySelector('[data-gold-reserve-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    if (!config.variantId) return;

    var buttons = Array.prototype.slice.call(
      panel.querySelectorAll('.gold-qty__btn'),
    );
    var totalEl = panel.querySelector('[data-gold-total]');
    var submit = panel.querySelector('[data-gold-submit]');
    var errorEl = panel.querySelector('.gold-panel__error');
    var qtyInput = panel.querySelector('[data-gold-qty-input]');
    var propEls = Array.prototype.slice.call(
      panel.querySelectorAll('[data-gold-prop]'),
    );

    var totals = config.totals || {};
    var qty = config.defaultQuantity || 1;
    var perPersonCap = Number(config.perPersonCap) || 0;

    function paint() {
      buttons.forEach(function (btn) {
        var on = Number(btn.getAttribute('data-gold-qty')) === qty;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (qtyInput) qtyInput.value = String(qty);
      if (totalEl && totals[qty]) totalEl.textContent = totals[qty];
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        qty = Number(btn.getAttribute('data-gold-qty')) || 1;
        showError('');
        paint();
      });
    });

    /* Hide the sticky bar while typing — iOS moves the visual viewport and
       fixed bars land on the keyboard / focused field. */
    propEls.forEach(function (el) {
      el.addEventListener('focus', function () {
        var bar = root.querySelector('[data-gold-stickybar]');
        if (bar) {
          bar.setAttribute('data-hidden', 'true');
          bar.setAttribute('aria-hidden', 'true');
          if ('inert' in bar) bar.inert = true;
        }
      });
      el.addEventListener('blur', function () {
        /* Sticky observer will restore visibility when appropriate. */
      });
    });

    function cartHost() {
      return (
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification')
      );
    }

    function cartSections() {
      var host = cartHost();
      if (host && typeof host.getSectionsToRender === 'function') {
        return host
          .getSectionsToRender()
          .map(function (section) {
            return section.section || section.id;
          })
          .join(',');
      }
      return 'cart-drawer,cart-icon-bubble';
    }

    function paintBubble(state) {
      if (window.Cana && typeof window.Cana.paintCartBubble === 'function') {
        window.Cana.paintCartBubble(
          state.sections && state.sections['cart-icon-bubble'],
          state.item_count,
        );
        return;
      }
      if (!state.sections || !state.sections['cart-icon-bubble']) return;

      var bubble = document.querySelector('#cartQuantity');
      if (!bubble) return;

      var parsed = new DOMParser().parseFromString(
        state.sections['cart-icon-bubble'],
        'text/html',
      );
      var count = parsed.querySelector('.cart-count-bubble span[aria-hidden]');
      bubble.textContent = count ? count.textContent.trim() : '';
    }

    function renderCart(state) {
      paintBubble(state);

      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
        try {
          drawer.renderContents(state);
          return;
        } catch (e) {
          /* fall through */
        }
      }

      var inner = document.querySelector('#CartDrawer');
      if (drawer && inner && state.sections && state.sections['cart-drawer']) {
        var html = drawer.getSectionInnerHTML(
          state.sections['cart-drawer'],
          '#CartDrawer',
        );
        if (html != null) inner.innerHTML = html;
        var drawerInner = document.querySelector('#DrawerInner');
        if (drawerInner) drawerInner.classList.remove('is-empty');
        drawer.classList.remove('is-empty');

        var checkout = document.querySelector('#CartDrawer-Checkout');
        if (checkout && typeof state.item_count === 'number') {
          if (state.item_count > 0) checkout.removeAttribute('disabled');
          else checkout.setAttribute('disabled', 'disabled');
        }

        drawer.open(submit);
        return;
      }

      var notification = document.querySelector('cart-notification');
      if (notification && typeof notification.renderContents === 'function') {
        if (!state.key && state.items && state.items.length) {
          state.key = state.items[0].key;
        }

        if (typeof notification.setActiveElement === 'function') {
          notification.setActiveElement(submit);
        }
        notification.renderContents(state);
        return;
      }

      window.location = (window.routes && window.routes.cart_url) || '/cart';
    }

    function cartQtyForVariant(variantId, done) {
      fetch((window.routes && window.routes.cart_url) || '/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var existing = 0;
          (cart.items || []).forEach(function (item) {
            if (Number(item.variant_id) === Number(variantId)) {
              existing += Number(item.quantity) || 0;
            }
          });
          done(null, existing);
        })
        .catch(function (err) {
          done(err, 0);
        });
    }

    function validateRequired() {
      for (var i = 0; i < propEls.length; i++) {
        var el = propEls[i];
        if (el.required && !(el.value || '').trim()) {
          showError('Preencha ' + (el.getAttribute('data-gold-prop') || 'os campos obrigatórios') + '.');
          el.focus();
          return false;
        }
      }
      return true;
    }

    function addToCart(event) {
      if (event) event.preventDefault();
      if (submit && submit.getAttribute('aria-disabled') === 'true') return;

      showError('');
      if (!validateRequired()) return;

      if (submit) submit.setAttribute('aria-disabled', 'true');

      function postAdd() {
        var properties = {};
        propEls.forEach(function (el) {
          var key = el.getAttribute('data-gold-prop');
          var value = (el.value || '').trim();
          if (key && value) properties[key] = value;
        });

        var body = {
          items: [
            {
              id: config.variantId,
              quantity: qty,
            },
          ],
          sections: cartSections(),
          sections_url: window.location.pathname,
        };

        if (Object.keys(properties).length) body.items[0].properties = properties;

        var addUrl =
          (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        })
          .then(function (response) {
            return response.json().then(function (state) {
              return { ok: response.ok, state: state };
            });
          })
          .then(function (result) {
            var state = result.state;
            if (!result.ok || state.status) {
              showError(state.description || state.message || config.genericError);
              return;
            }
            renderCart(state);
          })
          .catch(function () {
            showError(config.genericError);
          })
          .finally(function () {
            if (submit) submit.removeAttribute('aria-disabled');
          });
      }

      if (perPersonCap > 0) {
        cartQtyForVariant(config.variantId, function (err, existing) {
          if (!err && existing + qty > perPersonCap) {
            showError(
              config.capError ||
                ('Máximo de ' + perPersonCap + ' garrafas por pessoa neste carrinho.'),
            );
            if (submit) submit.removeAttribute('aria-disabled');
            return;
          }
          postAdd();
        });
      } else {
        postAdd();
      }
    }

    if (form) {
      form.addEventListener('submit', addToCart);
    } else if (submit) {
      submit.addEventListener('click', addToCart);
    }

    paint();
  }

  /* ----------------------------------------------------------- sticky CTA ---
     IntersectionObserver rather than a scroll handler: no per-frame work, and
     hiding the bar costs one compositor transform. */
  function initStickyBar(root) {
    var bar = root.querySelector('[data-gold-stickybar]');
    if (!bar) return;

    /* No observer, no way to know when the panel is on screen — and a bar that
       can never hide sits on top of the panel's own submit button. Hiding it is
       the safe end of that trade: the page keeps a working CTA either way. */
    if (!('IntersectionObserver' in window)) {
      bar.setAttribute('data-hidden', 'true');
      bar.setAttribute('aria-hidden', 'true');
      if ('inert' in bar) bar.inert = true;
      return;
    }

    /* The panel this bar belongs to, not whatever element happens to own the
       id `reserva`. The bar and its panel are rendered by the same section, so
       they always share a root — the id lookup is only a last resort for
       markup that predates the scoped class. */
    var target =
      root.querySelector('.gold-reserva') || document.querySelector('#reserva');
    if (!target) return;

    function setHidden(hidden) {
      bar.setAttribute('data-hidden', hidden ? 'true' : 'false');
      bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      if ('inert' in bar) bar.inert = !!hidden;
      var cta = bar.querySelector('a, button');
      if (cta) {
        if (hidden) cta.setAttribute('tabindex', '-1');
        else cta.removeAttribute('tabindex');
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        setHidden(entries[0].isIntersecting);
      },
      { rootMargin: '-40% 0px 0px 0px' },
    );

    observer.observe(target);
  }

  /* -------------------------------------------------------------- marquee ---
     The band runs an infinite transform animation. Left to itself that keeps a
     compositor layer live for the whole session, animating something nobody is
     looking at — on a phone, battery spent for no picture. Pause it whenever the
     band is off screen.

     Both copies are toggled in the same callback so they stay in phase; they run
     the same keyframes from the same start, and the seam only stays invisible
     while they agree.

     prefers-reduced-motion already drops the animation entirely in
     gold-reserva.css, so this only ever reaches motion-on users. */
  function initMarquee(root) {
    var band = root.querySelector('.gold-marquee');
    if (!band || !('IntersectionObserver' in window)) return;

    var runs = Array.prototype.slice.call(
      band.querySelectorAll('.gold-marquee__run'),
    );
    if (!runs.length) return;

    var observer = new IntersectionObserver(function (entries) {
      var state = entries[0].isIntersecting ? 'running' : 'paused';
      runs.forEach(function (run) {
        run.style.animationPlayState = state;
      });
    });

    observer.observe(band);
  }

  /* ----------------------------------------------------------------- boot --- */
  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-gold-section]');
    var inits = [
      initSubnav,
      initNotes,
      initSpecimen,
      initReserve,
      initStickyBar,
      initMarquee,
    ];

    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-gold-init') === 'true') return;
      root.setAttribute('data-gold-init', 'true');

      /* Sections are independently removable in the theme editor, and each of
         these touches a different one. Isolate them so a throw in any single
         section cannot leave the rest of the page without its behaviour. */
      inits.forEach(function (init) {
        try {
          init(root);
        } catch (error) {
          console.error('[gold-reserva]', init.name, error);
        }
      });
    });
  }

  window.canaOnReady(function () {
    boot(document);
  });

  /* The theme editor swaps a single section back in without reloading, so the
     new markup has to be wired up again. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  window.Cana = window.Cana || {};
  window.Cana.goldSpecimenKey = specimenKey;
})();
